import { useState } from 'react';
import { motion } from 'motion/react';
import { BarChart3, Users, Settings } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import UserManager from './UserManager';
import BudgetSettings from './BudgetSettings';
import { lightImpact } from '@/utils/haptics';

type AdminTab = 'dashboard' | 'users' | 'settings';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');

  const tabs: { id: AdminTab; label: string; icon: typeof BarChart3 }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'settings', label: 'Budget', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tab Bar */}
        <div className="flex gap-1 p-1 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              whileTap={{ scale: 0.97 }}
              onClick={async () => {
                await lightImpact();
                setActiveTab(tab.id);
              }}
              className={`relative flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-colors flex-shrink-0 min-h-12
                ${activeTab === tab.id
                  ? 'text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }
              `}
            >
              {activeTab === tab.id && (
                <motion.div
                  layoutId="adminTab"
                  className="absolute inset-0 rounded-xl gradient-bg shadow-glow"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
              <tab.icon className="w-4 h-4 relative z-10" />
              <span className="relative z-10">{tab.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'dashboard' && <AdminDashboard />}
          {activeTab === 'users' && <UserManager />}
          {activeTab === 'settings' && <BudgetSettings />}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage;
